import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import pluralize from "pluralize";
import { nanoid } from "nanoid";
import { useDispatch, useSelector } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import { toast } from "react-toastify";
import TextareaAutosize from "react-autosize-textarea";

import SwitchButton from "src/components/switchButton";
import { TabBar } from "src/components/tabbar";
import Actions from "src/components/actionbar";
import Toast from "src/components/toast";
import Text from "src/components/text";
import Slider from "src/components/slider";
import { AutoGrowInput, Input } from "src/components/input";
import Button from "src/components/button";
import Reveal from "src/components/animation";
import { SaveLearningsDialog } from "src/pages/coaching/manage/courses/edit/components/menu";

import { AssessmentParameter, AssessmentConfiguration, AssessmentSubParameter } from "src/types/coaching/assessmentConfiguration";
import { deleteAssessmentConfiguration, discardAssessmentConfiguration, fetchAssessmentConfiguration, invalidateAssessmentConfigurationDeletionState, invalidateAssessmentConfigurationEditState, invalidateAssessmentConfigurationUpdateState, invalidatePublishAssessmentConfiguration, publishAssessmentConfiguration, updateAssessmentConfiguration, updateAssessmentConfigurationProperty } from "src/redux/actions/coaching/settings";
import { hideSideMenu } from "src/redux/actions/hub";

import _ from "lodash";
import cx from "classnames";
import styles from "./preview.module.scss";


interface ScoreCardPreviewProps {}

type ActiveDialog = 'discard' | 'delete' | null;

const ScoreCardPreview: React.FC<ScoreCardPreviewProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id;

  const updateRef = useRef(false);
  const updateActionRef = useRef<"save" | "discard" | null>(null);

  const {loading, loaded }  = useSelector((state: any) => {
    const scoreCardsState = _.get(state, `coaching.settings.scorecards.scorecard[${id}]`);
    return {
      loading: _.get(scoreCardsState, 'loading', false),
      loaded: _.get(scoreCardsState, 'loaded', false),
    }
  }, _.isEqual);

  const scorecard = useSelector((state: any) => { 
    return _.get(state.coaching.settings.scorecards, 'edit.item') as AssessmentConfiguration;
  });

  const persistedScorecard = useSelector((state: any) => {
    return _.get(state.coaching.settings.scorecards, `scorecard[${id}].item`) as AssessmentConfiguration;
  }
  );

  const parameters = useMemo(() => {
    return scorecard && scorecard.parameters ? scorecard.parameters : [];
  }, [scorecard]);

  const [selectedParamIndex, setSelectedParamIndex] = useState(0);
  const selectedParameter = parameters[selectedParamIndex] || null;

  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedSubParameter, setSelectedSubParameter] = useState<AssessmentSubParameter[]>([]);
  const [showSubParamSlider, setShowSubParamSlider] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);
  const [paramsValue, setParamsValue] = useState(selectedSubParameter[activeTab]?.criteria || "");

  const CALL_TYPE_OPTIONS = [
    { label: 'all type', value: 'all' },
    { label: 'cold calling', value: 'cold_call' },
    { label: 'discovery', value: 'discovery' },
    { label: 'follow-up', value: 'follow_up' },
  ];

  const callType = useMemo(() => {
    return CALL_TYPE_OPTIONS.find((item) => item.value === scorecard?.callType?.[0]) || CALL_TYPE_OPTIONS[0];
  }, [scorecard]);

  const { updating, updated, error } = useSelector((state) => {
    const scoreCardsUpdateState = _.get(state, `coaching.settings.scorecards.update[${id}]`);

    const updating = _.get(scoreCardsUpdateState, 'updating', false);   
    const updated = _.get(scoreCardsUpdateState, 'updated', null);
    const error = _.get(scoreCardsUpdateState, 'error', null);

    return {
      updating,
      updated,
      error
    };
  }, _.isEqual);

  const { publishing, published } = useSelector((state) => {
    const scoreCardsUpdateState = _.get(state, `coaching.settings.scorecards.publish[${id}]`);

    const publishing = _.get(scoreCardsUpdateState, 'publishing', false);   
    const published = _.get(scoreCardsUpdateState, 'published', null);  

    return {
      publishing,
      published
    }
  }, _.isEqual);

  const name = useMemo(() => {
    return _.get(scorecard, `name`, ''); 
  }, [scorecard]);

  const description = useMemo(() => {
    return _.get(scorecard, `description`, '');
  }, [scorecard]);

  const hasChanges = useMemo(() => {
    return _.get(scorecard, `hasChanges`) as boolean;
  }, [scorecard]);

  const hasPublished = useMemo(() => {
    return _.get(scorecard, `published`) as boolean;
  } , [scorecard]);

  const defaultConfiguration = useMemo(() => {
    return _.get(scorecard, `defaultConfiguration`) as boolean;
  }, [scorecard]);

  const systemDefined = useMemo(() => {
    return _.get(scorecard, `systemDefined`) as boolean;
  }, [scorecard]);
  
  
  const handleAddSubParameter = ({ name, description }: { name: string; description: string }) => {
    const newSub: AssessmentSubParameter = {
      uid: `${nanoid(6)}`,
      name,
      criteria: description,
    };

    const newSubParameterIndex =
    parameters[selectedParamIndex].parameters.length;

    const updatedParameters = parameters.map((parameter, index) => {
      if (index === selectedParamIndex) {
        return {
          ...parameter,
          parameters: [...parameter.parameters, newSub],
        };
      }
      return parameter;
    });

    handlePropertyChange("parameters", updatedParameters);
    setActiveTab(newSubParameterIndex);
    setShowSubParamSlider(false);
  };

  const handleDeleteParameter = (index: number) => {
    if (parameters.length <= 1) return;
    if (index < 0 || index >= parameters.length) return;

    const updatedParameters = [
      ...parameters.slice(0, index),
      ...parameters.slice(index + 1),
    ];

    handlePropertyChange("parameters", updatedParameters);

    setSelectedParamIndex((prev) =>
      prev === index ? 0 : prev > index ? prev - 1 : prev
    );
  };

  const handleAddParameter = () => {
    const newParam: AssessmentParameter = {
      uid: `${nanoid(6)}`,
      name: "Untitled",
      weightage: 0,
      parameters: [
        {
          uid: `${nanoid(6)}`,
          name: "Untitled",
          criteria: "",
        }
      ],
    };
    
    const updatedParameters = [...parameters, newParam];
    handlePropertyChange("parameters", updatedParameters);
    setSelectedParamIndex(updatedParameters.length - 1);
    setActiveTab(0);
    setSelectedSubParameter(newParam.parameters);
  };

  const handleRemoveSubParameter = (removeIndex: number) => {
    const parameter = parameters[selectedParamIndex];
    if (!parameter) return;

    const subParams = parameter.parameters ?? [];
    if (subParams.length <= 1) return;
    if (removeIndex < 0 || removeIndex >= subParams.length) return;

    const updatedSubParams = [
      ...subParams.slice(0, removeIndex),
      ...subParams.slice(removeIndex + 1),
    ];

    const updatedParameters = parameters.map((param, index) =>
      index === selectedParamIndex
        ? { ...param, parameters: updatedSubParams }
        : param
    );

    handlePropertyChange("parameters", updatedParameters);
    setActiveTab((prev) => Math.max(prev - 1, 0));
  };

  const validateScorecard = (
    name: string,
    description: string,
    parameters: AssessmentParameter[]
  ): string[] => {
    const errors: string[] = [];
  
    if (!_.trim(name)) {
      errors.push("Assessment Configuration name cannot be empty");
    }
  
    if (!_.trim(description)) {
      errors.push("Assessment Configuration description cannot be empty");
    }
  
    for (const parameter of parameters) {
      if (!_.trim(parameter.name)) {
        errors.push("Parameter name cannot be empty");
      }
  
      for (const subparameter of parameter.parameters) {
        if (!_.trim(subparameter.name)) {
          errors.push("Sub-parameter name cannot be empty");
        }
  
        if (!_.trim(subparameter.criteria)) {
          errors.push("Sub-parameter description cannot be empty");
        }
      }
    }
  
    return errors;
  };
  
  
  const handleSave = () => {

    updateActionRef.current = "save";

    const validationErrors = validateScorecard(
      name,
      description,
      parameters
    );
    
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        toast(
          <Toast.Failure
            heading="Failed"
            message={error}
          />
        );
      });
      return;
    }

    const request = {
      name: name,
      description: description,
      callType: [callType.value],
      systemDefined: systemDefined || false,
      defaultConfiguration: defaultConfiguration || false,
      parameters: parameters.map((param) => ({
        uid: param.uid,
        name: param.name,
        weightage: param.weightage,
        parameters: param.parameters.map((sub) => ({
          uid: sub.uid,
          name: sub.name,
          criteria: sub.criteria,
        })),
      })),
    };
    dispatch(
      updateAssessmentConfiguration({
        id: scorecard.id!,
        request: request,
      }),
    );
  };

  const handlePublish = () => {

    const validationErrors = validateScorecard(
      name,
      description,
      parameters
    );
    
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        toast(
          <Toast.Failure
            heading="Failed"
            message={error}
          />
        );
      });
      return;
    }

    dispatch(publishAssessmentConfiguration({ id: id! }));
  }

  const isDefault = defaultConfiguration!;
  const isReadOnly = systemDefined;

  const handleChangeCallType = (option) => {
    handlePropertyChange("callType", [option.value]);
  }

  const scorecardUpdated = useMemo(() => {
    if (!scorecard || !persistedScorecard) return false;
  
    const omittedFields = [
      "id", "uid", "hasChanges", "systemDefined", "published", "createdDate", "modifiedDate"
    ];
  
    const fieldsChanged = !_.isEqual(
      _.omit(scorecard, omittedFields),
      _.omit(persistedScorecard, omittedFields)
    );
  
    const parametersChanged = !_.isEqual(
      (scorecard.parameters ?? []).map(parameter => ({
        ..._.omit(parameter, "uid"),
        parameters: (parameter.parameters ?? []).map(subparameter => ({ ..._.omit(subparameter, "uid") }))
      })),
      (persistedScorecard.parameters ?? []).map(parameter => ({
        ..._.omit(parameter, "uid"),
        parameters: (parameter.parameters ?? []).map(subparameter => ({ ..._.omit(subparameter, "uid") }))
      }))
    );
  
    return fieldsChanged || parametersChanged;
  }, [scorecard, persistedScorecard]);  

  const handleBack = () => {
    dispatch(hideSideMenu(false));
    if (!scorecardUpdated) {
      navigate('/settings/call-assessments');
      return;
    }
    setShowSaveChangesDialog(true);
  }

  const handleSwitchDefault = (scorecard: AssessmentConfiguration) => {
   
  }

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(updateAssessmentConfigurationProperty({
        id: scorecard.id!,
        property,
        value,
      }),
    );
  };  

  const handleParameterNameChange = (e: any) => {
    let value = e.target.value.replace(/\n/g, "");

    const updatedParameters = parameters.map((parameter, index) => {
      if(index === selectedParamIndex) {
        return { ...parameter, name: value };
      }
      return parameter;
    });

    handlePropertyChange("parameters", updatedParameters);
  }

  const handleParameterWeightageChange = (event: any) => {
    const value = event.target.value;
    const numericValue = parseFloat(value);

    if (value === "") {
      const updatedParameters = parameters.map((parameter, index) => {
        if (index === selectedParamIndex) {
          return { ...parameter, weightage: 0 };
        }
        return parameter;
      });
  
      handlePropertyChange("parameters", updatedParameters);
      return;
    }

    if (numericValue > 0 && numericValue <= 100) {
      const updatedParameters = parameters.map((parameter, index) => {
        if (index === selectedParamIndex) {
          return { ...parameter, weightage: numericValue };
        }
        return parameter;
      });
  
      handlePropertyChange("parameters", updatedParameters);
    }
  }

  const handleSubParameterNameChange = (e: any) => {
    let value = e.target.value.replace(/\n/g, "");
    const updatedParameters = parameters.map((parameter, index) => {
      if(index === selectedParamIndex) {
        const updatedSubParameters = parameter.parameters.map((subParam, subIndex) => {
          if(subIndex === activeTab) {
            return { ...subParam, name: value };  
          }
          return subParam;
        });
        return { ...parameter, parameters: updatedSubParameters };
      }
      return parameter;
    });

    handlePropertyChange("parameters", updatedParameters);
  }

  const handleEvaluationCriteriaChange = (e: any) => {
    let value = e.target.value;
    setParamsValue(value);
    const updatedParameters = parameters.map((parameter, index) => {
      if(index === selectedParamIndex) {
        const updatedSubParameters = parameter.parameters.map((subParam, subIndex) => {
          if(subIndex === activeTab) {
            return { ...subParam, criteria: value };
          }
          return subParam;
        });
        return { ...parameter, parameters: updatedSubParameters };
      }
      return parameter;
    });

    handlePropertyChange("parameters", updatedParameters);
  }

  const handleDeleteConfirm = () => {
    dispatch(deleteAssessmentConfiguration({ id: scorecard.id! }));
    dispatch(hideSideMenu(false));
  };

  const getNameError = () => {
    if (_.isEmpty(_.trim(name))) 
     return 'Please provide a valid name';

    if (_.size(name) > 100) {
      return 'Name cannot be greater than 100 characters';
    }
    return null;
  };

  const getDescriptionError = () => {
    if (_.isEmpty(_.trim(description))) 
      return 'Please provide a valid description';
    if (_.size(description) > 200) {
      return 'Description cannot be greater than 200 characters';
    }
    return null;
  };

  const nameError = getNameError();
  const descriptionError = getDescriptionError(); 

  useEffect(() => {
    
    return () => {
      dispatch(invalidateAssessmentConfigurationEditState());
    };
  }, []);

  useEffect(() => {
    if(id && !scorecard) {
      dispatch(hideSideMenu(true));
      dispatch(fetchAssessmentConfiguration(id));
    }
  }, [id]);

  useEffect(() => {
    if (parameters.length > 0 && selectedParamIndex >= parameters.length) {
      setSelectedParamIndex(0);
    }
  }, [parameters, selectedParamIndex]);

  useEffect(() => {
    if (selectedParameter?.parameters) {
      setSelectedSubParameter(selectedParameter.parameters);
    }
  }, [selectedParameter]);

  useEffect(() => {
    if (published) {
      setIsPublished(true);
    }
  }, [published]);

  useEffect(() => {
    if (updateRef.current && !updating) {
      const action = updateActionRef.current;

      if (action === "save") {
        if (!error) {
          toast(
            <Toast.Sucess
              heading="Success"
              message={
                <>
                  The assessment configuration{" "}
                  <span className={styles.scorecardName}>
                    {scorecard.name}
                  </span>{" "}
                  has been saved successfully.
                </>
              }
            />
          );
        } else {
          toast(
            <Toast.Failure
              heading="Failed"
              message={
                <>
                  The assessment configuration{" "}
                  <span className={styles.scorecardName}>
                    {scorecard.name}
                  </span>{" "}
                  could not be saved.
                </>
              }
            />
          );
        }
      }
  
      if (action === "discard" && !error) {
        toast(
          <Toast.Sucess
            heading="Discarded"
            message={
              <>
                Changes to{" "}
                <span className={styles.scorecardName}>
                  {scorecard.name}
                </span>{" "}
                were discarded successfully.
              </>
            }
          />
        );
      }
      updateActionRef.current = null;
    }
    updateRef.current = updating;
  }, [updating, error, scorecard?.name]);

  useEffect(() => {
    setParamsValue(selectedSubParameter[activeTab]?.criteria || "");
  }, [selectedSubParameter, activeTab]);

  if(loading || !scorecard) {
    return <div className={styles.loading}><ScorecardPreviewPlaceholder /></div>;
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <div className={styles.header}>
          <i
            className={cx("paperflite-left-arrow", styles.backIcon)}
            onClick={handleBack}
          />
          <div className={styles.title}>
          <input
            className={styles.nameInput}
            value={name}
            disabled={isReadOnly}
            onChange={(e) => {
              const value = e.target.value;
              handlePropertyChange("name", value);
            }}
          />
          </div>
        </div>
        {!isReadOnly && (
          <Actions.Row>
            <Actions.IconButton
              icon="paperflite-delete"
              tooltip="Delete"
              onClick={() => {
                setActiveDialog("delete");
              }}
            />

            {hasPublished && (
              <Actions.Button
              className={styles.iconButton}
              width={2}
              icon="paperflite-close"
              title="discard"
              onClick={() => {
                setActiveDialog("discard");
              }}
            />
            )}

            <Actions.Button
              className={cx(styles.iconButton, styles.editStudio)}
              icon="paperflite-tick-unselected"
              testId="save-scorecard-button"
              title={updating ? "Saving..." : "Save"}
              disabled={updating || publishing || !!nameError || !!descriptionError}
              onClick={handleSave}
              width={2}
            />

            <Actions.Button
              className={cx(styles.iconButton, styles.publish)}
              icon="paperflite-rocket"
              title={publishing ? "Publishing..." : "Publish"}
              width={2}
              disabled={published || !hasChanges || updating || publishing}
              onClick={handlePublish}
            />
          </Actions.Row>
        )}
      </div>
      <Reveal show={!!nameError}>
        {nameError && (
          <div className={styles.error}>
            <i className="paperflite-danger" />
            {nameError}
          </div>
        )}
      </Reveal>
      <div className={styles.description}>
        <input
          className={styles.descriptionInput}
          value={description}
          disabled={isReadOnly}
          onChange={(e) => {
            const value = e.target.value;
            handlePropertyChange("description", value);
          }}
        />
        <Reveal show={!!descriptionError}>
          {descriptionError && (
            <div className={styles.error}>
              <i className="paperflite-danger" />
              {descriptionError}
            </div>
          )}
        </Reveal>
          </div>
      <div className={styles.body}>
        <div className={styles.left}>
          <div
            className={cx(styles.toggleButton, {
              [styles.disabled]: isReadOnly,
            })}
            onClick={() => !isReadOnly && handleSwitchDefault(scorecard)}
          >
            <SwitchButton
              id="defaultScoreCard"
              label="set as default"
              className={cx(styles.switch, {
                [styles.active]: isDefault,
                [styles.disabled]: isReadOnly,
              })}
              checked={isDefault}
              disabled={isReadOnly}
              handleCheck={() => {
                handlePropertyChange("defaultConfiguration", !isDefault);
              }}
            />
            <div
              className={cx(styles.callType, { [styles.disabled]: isReadOnly })}
            >
              <CallTypeDropdown
                selected={callType}
                readOnly={isReadOnly}
                options={CALL_TYPE_OPTIONS}
                onChange={handleChangeCallType}
              />
            </div>
          </div>
          <div className={styles.parameters}>
            <div className={styles.title}>
              {" "}
              {`parameters (${_.size(parameters)})`}
            </div>
            <div className={styles.parametersList}>
              {parameters.map((paramGroup, index) => {
                const subParamCount = paramGroup.parameters?.length || 0;

                return (
                  <div
                    key={index}
                    className={cx(
                      styles.paramGroup,
                      selectedParamIndex === index && styles.active,
                    )}
                    onClick={() => setSelectedParamIndex(index)}
                  >
                    <div className={styles.paramHeader}>
                      <div className={styles.paramGroupName}>
                        <Text text={paramGroup.name || "Unititled"} lines={1} />
                      </div>

                      <div className={styles.meta}>
                        <span className={styles.item}>
                          {subParamCount}{" "}
                          {pluralize("sub-parameter", subParamCount)}
                        </span>
                        <span className={styles.item}>
                          Weightage: {paramGroup.weightage}
                        </span>
                      </div>
                    </div>
                    {index > 0 && !scorecard.systemDefined && (
                      <i
                        className={cx("paperflite-delete", styles.deleteIcon)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteParameter(index);
                        }}
                      />
                    )}
                  </div>
                );
              })}
              {!scorecard.systemDefined && (
                <div
                  className={styles.addParameter}
                  onClick={handleAddParameter}
                >
                  <i
                    className={cx(
                      "paperflite-add",
                      styles.addParamIcon,
                    )}
                  />
                  <span>add new parameter</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.info}>
            <div className={styles.inputValue}>
              <div className={styles.name}>Parameter name</div>
              <div className={styles.inputWrapper}>
                <input
                  className={cx(styles.parameterNameInput, {
                    [styles.readable]: !scorecard.systemDefined,
                  })}
                  type="text"
                  placeholder="Enter parameter name"
                  value={selectedParameter?.name || ""}
                  onChange={handleParameterNameChange}
                  disabled={scorecard.systemDefined}
                  maxLength={100}
                />
              </div>
            </div>
            <div className={styles.inputValue}>
              <div className={styles.name}>Weightage:</div>
              <div className={styles.inputWrapper}>
                <input
                  className={cx(styles.weightageInput, {
                    [styles.readable]: !scorecard.systemDefined,
                  })}
                  type="text"
                  pattern='[0-9]*'
                  placeholder="00"
                  value={selectedParameter?.weightage}
                  onChange={handleParameterWeightageChange}
                  disabled={scorecard.systemDefined}
                />
              </div>
            </div>
          </div>
          <div className={styles.subParameters}>
            <div className={styles.subheading}>
              sub-
              {pluralize(
                "parameter",
                selectedParameter?.parameters?.length || 0,
              )}{" "}
              ({selectedParameter?.parameters?.length || 0})
            </div>
            <div className={styles.description}>
              Define the aspect of performance to be assessed for each
              sub-parameter.
            </div>
            <div className={styles.tabRow}>
              {!scorecard.systemDefined && (
                <AddButton
                  label="Add Sub-Parameter"
                  onClick={() => setShowSubParamSlider(true)}
                />
              )}
              <div className={styles.tabList}>
                <TabBar
                  className={styles.tabs}
                  gap={32}
                  active={activeTab}
                  onSelectionChange={(tabIndex) => setActiveTab(tabIndex)}
                >
                  {selectedParameter?.parameters?.map((subParam, idx) => {
                    let content: React.ReactNode = subParam.name;

                    if (!scorecard.systemDefined) {
                      content = (
                        <AutoGrowInput
                          value={subParam.name}
                          className={styles.tabInput}
                          onChange={handleSubParameterNameChange}
                        />
                      );
                    }

                    return (
                      <div
                        key={subParam.uid}
                        className={cx(styles.tabItem, {
                          [styles.activeTab]: idx === activeTab,
                        })}
                      >
                        <div className={styles.tabInput}>{content}</div>
                        {!scorecard.systemDefined &&
                          idx === activeTab &&
                          selectedParameter?.parameters.length > 1 && (
                            <button
                              className={styles.remove}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSubParameter(idx);
                              }}
                            >
                              <i className="paperflite-close" />
                            </button>
                          )}
                      </div>
                    );
                  })}
                </TabBar>
              </div>
            </div>
            <div className={styles.details}>
              <div className={styles.subParamDetails}>Evaluates</div>
              <TextareaAutosize
                placeholder="e.g. how well the rep surfaced objections and addressed them with confidence, empathy, and relevant proof points."
                value={paramsValue}
                className={cx(styles.criteriaInput, {
                  [styles.readable]: !scorecard.systemDefined,
                })}
                disabled={scorecard.systemDefined}
                onChange={handleEvaluationCriteriaChange}
                onPointerEnterCapture={() => { }}
                onPointerLeaveCapture={() => { }}
              />
            </div>
          </div>
        </div>
      </div>
      <CreateSubParameterDialog
        show={showSubParamSlider}
        onClose={() => setShowSubParamSlider(false)}
        onCreate={handleAddSubParameter}
      />
      <DiscardAssessmentConfigurationDialog
        show={activeDialog === "discard"}
        scorecard={scorecard}
        onClose={() => {
          setActiveDialog(null);
        }}
        onConfirm={() => {
          updateActionRef.current = "discard";
          dispatch(discardAssessmentConfiguration({ id: scorecard.id! }))
        }}
      />

      <DeleteAssessmentConfigurationDialog
        show={activeDialog === "delete"}
        scorecard={scorecard}
        onClose={() => {
          setActiveDialog(null);
          dispatch(invalidateAssessmentConfigurationDeletionState());
          navigate("/settings/call-assessments");
          dispatch(hideSideMenu(false));
        }}
        onConfirm={handleDeleteConfirm}
      />

      {showSaveChangesDialog && (
        <SaveLearningsDialog
          show={showSaveChangesDialog}
          course={scorecard}
          onFinish={() => setShowSaveChangesDialog(false)}
          onClose={() => setShowSaveChangesDialog(false)}
          handleSave={handleSave}
          type="scorecards"
        />
      )}

      <PublishView isOpen={isPublished} scorecard={scorecard} onClose={() => setIsPublished(false)} />
    </div>
  );
}

export default ScoreCardPreview;


interface CallTypeDropdownProps {
  selected: any;
  options: any[];
  onChange: (option: any) => void;
  readOnly?: boolean;
}

export const CallTypeDropdown: React.FC<CallTypeDropdownProps> = ({
  selected,
  options,
  onChange, 
  readOnly
}) => {
  const selectedOption = useMemo(
    () => options.find(option => option.value === selected.value),
    [selected, options]
  );

  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (value: string) => {
    if (readOnly) return;
    const option = options.find(option => option.value === value);
    if (option) {
      onChange(option);
    }
  };

  return (
    <div className={styles.selectDropdown}>
      {readOnly && (
        <div className={styles.readonlyValue}>
          {selectedOption?.label}
        </div>
      )}
      {!readOnly && (
      <Dropdown show={!readOnly && isOpen} onToggle={(next) => {
        if (readOnly) return;
        setIsOpen(next);
      }}>
        <Dropdown.Toggle className={styles.button} as="div" onClick={() => {
            if (readOnly) return;
            setIsOpen(prev => !prev);
          }}>
          <span className={styles.callTypeLabel}>
            {selectedOption.label}
          </span>
          <span
            className={cx(styles.dropdownIconWrapper, {
              [styles.open]: isOpen,
            })}
          >
            <i className="paperflite-dropdown" />
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={styles.dropdown}
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: { offset: [-10, 6] },
              },
            ],
          }}
        >
          <div className={styles.dropdownTitle}>
          <div className={styles.dropdownHeader}>call type</div>
          <div className={styles.dropdownDescription}>Indicate the type of calls this framework is designed to assess.</div>
          </div>
          {options.map((option, index) => (
            <Dropdown.Item
              key={index}
              className={cx(styles.dropdownItem, {
                [styles.active]: option.value === selectedOption?.value,
              })}
              onClick={() => handleSelect(option.value)}
            > 
              <i
                className={cx(
                  option.value === selectedOption?.value
                    ? 'fa-regular fa-circle-check'
                    : 'fa-regular fa-circle',
                  styles.optionIcon
                )}
              />
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      )}
    </div>
  );
};

const CreateSubParameterDialog = ({show,onClose, onCreate}) => {
  const [name,setName] = useState('');
  const [description,setDescription] = useState('');
  const handleCreate = () => {
    if (!name.trim()) return;
    onCreate({ name, description });
    setName("");
    setDescription("");
  };

  const getNameError = () => {
    if (_.isEmpty(_.trim(name))) 
      return null;
    if (_.size(name) > 100) {
      return 'Name cannot be greater than 100 characters';
    }
    return null;
  };

  const nameError = getNameError();

  const canCreate =
  !_.isEmpty(_.trim(name)) &&
  !nameError

  return (
    <Slider.Dialog
      className={styles.createCollectionDialog}
      width={"380px"}
      show={show}
      onClose={()=> {}}
    >
      <Slider.Header
        className={styles.header}
        title="create new sub-parameter"
        message='Name the specific behavior or skill this sub-parameter is intended to evaluate.'
      />
      <Slider.Body className={styles.body}>
        <Input
          autoFocus
          className={styles.nameInput}
          placeholder="enter sub-parameter name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Reveal show={!!nameError}>
          {nameError && (
            <div className={styles.error}>
              <i className="paperflite-danger" />
              {nameError}
            </div>
          )}
        </Reveal>
        <div className={styles.input}>e.g. objection handling</div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text="Create"
          variant="primary"
          onClick={handleCreate}
          disabled={!canCreate}
        />
        <Button
          text="Cancel"
          variant="secondary"
          onClick={onClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

const DiscardAssessmentConfigurationDialog = ({  
  show,
  onClose,
  onConfirm,
  scorecard,
}) => {

  const dispatch = useDispatch();

  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(state.coaching, `settings.scorecards.update.['${scorecard.id}'].updating`) as boolean;
    const updated = _.get(state.coaching, `settings.scorecards.update.['${scorecard.id}'].updated`) as boolean;

    return { updating, updated };
  });

  useEffect(() => {
    if (updated) {
      dispatch(invalidateAssessmentConfigurationUpdateState());
      onClose();
    }
  }, [updated]);  

  return (
    <Slider.Dialog
      className={styles.createCollectionDialog}
      width={"380px"}
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        className={styles.header}
        title="discarding draft..."
        message={scorecard.name}
      />
      <Slider.Body className={styles.body}>
        <div className={styles.discardMessage}>
        Are you sure you want to discard changes to this assessment framework? This action cannot be undone.
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={updating ? "Discarding..." : "Confirm"}
          variant="primary"
          disabled={updating}
          onClick={onConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={updating}
          onClick={onClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

const DeleteAssessmentConfigurationDialog = ({  show,
  onClose,
  onConfirm,
  scorecard,
}) => {

  const {deleting, deleted} = useSelector((state: any) => {
    const scoreCardsDeleteState = _.get(state, `coaching.settings.scorecards.deletion[${scorecard.id!}]`);

    const deleting = _.get(scoreCardsDeleteState, 'deleting', false);   
    const deleted = _.get(scoreCardsDeleteState, 'deleted', null);

    return {
      deleting,
      deleted
    }
  }, _.isEqual);

  useEffect(() => {
    if (deleted) {
      onClose();
    }
  }, [deleted]);

  return (
    <Slider.Dialog
      className={styles.createCollectionDialog}
      width={"380px"}
      show={show}
      onClose={()=> {}}
    >
      <Slider.Header
        className={styles.header}
        title="deleting..."
        message={scorecard.name}
      />
      <Slider.Body className={styles.body}>
        <div className={styles.discardMessage}>
        Are you sure you want to delete this assessment framework? This action cannot be undone.
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          variant="primary"
          disabled={deleting}
          onClick={onConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={onClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface AddButtonProps {
  onClick: () => void;
  label?: string;
}

export const AddButton: React.FC<AddButtonProps> = ({ onClick, label = "Add Scorecard" }) => {
  return (
    <div className={styles.addSectionButton} onClick={onClick}>
      <i className={cx(styles.icon, "paperflite-add")} />
      <div className={cx(styles.cardName, styles.add)}>{label}</div>
      <div className={styles.seperator}></div>
    </div>
  );
};

const ScorecardPreviewPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.container, styles.placeholder)}>
      <div className={styles.subtitle}>
        <div className={styles.header}>
          <i className={cx("paperflite-left-arrow", styles.skeletonBackIcon)} />
          <div className={cx(styles.skeletonTitle, styles.block)} />
        </div>
      </div>
      <div className={cx(styles.skeletonDescription, styles.block)} />
      <div className={styles.body}>
        <div className={styles.left}>
          <div className={styles.parameters}>
            <div className={styles.toggle}>
              <div className={cx(styles.setDefault, styles.block)} />
              <div className={cx(styles.callType, styles.block)} />
            </div>

            <div className={styles.title}>parameters (1)</div>

            <div className={styles.parametersList}>
              {[1, 2, 3].map((_, i) => (
                <div key={i} className={styles.skeletonParamGroup}>
                  <div className={cx(styles.skeletonParamName, styles.block)} />
                  <div className={cx(styles.skeletonMeta, styles.block)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.right}>
          <div className={styles.info}>
            <div className={styles.skeletonInputGroup}>
              <div className={styles.skeletonDetail}>Parameter name</div>
              <div className={cx(styles.skeletonInput, styles.block)} />
            </div>

            <div className={styles.skeletonInputGroup}>
              <div className={styles.skeletonDetail}>Weightage</div>
              <div className={cx(styles.skeletonInputSmall, styles.block)} />
            </div>
          </div>

          <div className={styles.subParameters}>
            <div className={styles.subParameterTitle}>
              sub-parameters (1)
            </div>

            <div className={styles.description}>
              Define the aspect of performance to be assessed for each sub-parameter.
            </div>

            <div className={styles.subParametersList}>
              {[1, 2, 3].map((_, i) => (
                <div key={i} className={cx(styles.skeletonSubParameterGroup, styles.block)}>
                </div>
              ))}
            </div>

            <div className={styles.skeletonTabRow}>
              <div className={styles.evaluate}>Evaluates</div>
              <div className={cx(styles.skeletonTextarea, styles.block)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PublishViewProps {
  isOpen: boolean;
  scorecard: AssessmentConfiguration;
  onClose: () => void;
}

export const PublishView: React.FC<PublishViewProps> = ({
  isOpen,
  scorecard,
  onClose
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [closePublish, setClosePublish] = useState(false);

  const { published } = useSelector((state: any) => {
    return _.get(state.coaching.settings.scorecards.publish, `${scorecard.id}`) || {};
  });

  const publishTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 100,
    onRest: () => {
      if (closePublish) {
        setClosePublish(false);
        onClose();
      }
    },
  });

  const handleExit = () => {
    dispatch(hideSideMenu(false));
    navigate(`/settings/call-assessments`);
  }

  useEffect(() => {
    if (published) {
      dispatch(invalidatePublishAssessmentConfiguration());
    }
  }, [published]);

  return publishTransitions(
    (transitionStyles, item) =>
      item && (
        <animated.div
          className={styles.publishContainer}
          style={transitionStyles}
        >
          <div className={styles.left}>
            <img className={styles.title} src="/images/coaching/congratulations.svg" alt="Congratulations" />
            <p className={styles.message}>
              you’ve successfully published the assessment configuration “{scorecard.name || 'Untitled Podcast'}”.
            </p>
            <div className={styles.actions}>
              <Button
                className={cx(styles.action, styles.primary)}
                variant="primary"
                icon="paperflite-right-arrow"
                text="view assessment configuration details"
                onClick={() => setClosePublish(true)}
              />
              <Button
                className={styles.action}
                variant="secondary"
                icon="paperflite-close"
                text="exit"
                onClick={handleExit}
              />
            </div>
          </div>
          <div className={styles.right}>
            <img className={styles.illustration} src="/images/coaching/confetti.gif" />
          </div>
        </animated.div>
      ),
  );
};



